
import type { CharacterSlot, Characters } from "../net/messages";
import { Gait } from "../sim/movement";
import { CreatureMeshRenderer, hairEntityId } from "../render/creaturemesh";
import { drawCreature, type CreatureFrame, type CreaturePalette } from "../render/creature";
import { lookAt, multiply, perspective, type Mat4 } from "../render/view";
import { INTRO_ART, installIntroStyle, introGround } from "./intro";
import { elem, styleInstaller } from "./chrome";

export interface CharacterSelectActions {
  /** Play the character in this slot. */
  onPlay?: (slot: number) => void;
  /** Delete the character in this slot, with the account password. */
  onDelete?: (slot: number, confirm: string) => void;
  /** The player wants to fill this empty slot. The creation screen takes over. */
  onCreate?: (slot: number) => void;
  /** Leave character select without selecting or deleting a character. */
  onExit?: () => void;
}

/** `stats.Job` names, in job order. Wire values 0..3. */
export const JOB_NAMES: readonly string[] = [
  "Warrior",
  "Assassin",
  "Sura",
  "Shaman",
];

export const EMPIRE_NAMES: readonly string[] = [
  "",
  "Shinsoo Kingdom",
  "Chunjo Kingdom",
  "Jinno Kingdom",
];

export const SELECT_TEXT = {
  /** SELECT_SELECT, on `start_button` -- locale_interface.txt:293. */
  start: "Start",
  /** SELECT_CREATE, on `create_button` -- :282. */
  create: "Create",
  /** SELECT_DELETE, on `delete_button` -- :283. */
  delete: "Delete",
  /** SELECT_EXIT, on `exit_button` -- :286. */
  exit: "Close",
  /** SELECT_NAME / SELECT_LEVEL -- :290, :288. */
  name: "Name",
  level: "Level",
  /** SELECT_PLAYTIME -- :292. */
  playtime: "Playtime (min.)",
  /** SELECT_NO_GUILD / SELECT_NOT_JOIN_GUILD -- :291 and locale_game.txt:429. */
  noGuild: "No Guild",
  /** SELECT_EMPTY_SLOT, what `RequestDeleteCharacter` says on an empty slot -- locale_game.txt:426. */
  emptySlot: "This is an empty field.",
  emptySlotCard: "Empty slot",
  /** SELECT_DO_YOU_DELETE_REALLY, the questiondialog's message -- locale_game.txt:425. */
  deleteReally: "Do you want to continue?",
  /** INPUT_PRIVATE_CODE_DIALOG_TITLE -- locale_game.txt:216. */
  deleteTitle: "Delete character",
  /** SELECT_DELEING, shown the moment the request goes out -- locale_game.txt:423. */
  deleting: "Character will be deleted.",
} as const;

export const BOARD_STAT_ROWS: ReadonlyArray<
  readonly [string, "ht" | "iq" | "st" | "dx", string]
> = [
  ["HP", "ht", "#d8584b"],
  ["INT", "iq", "#d884c4"],
  ["STR", "st", "#ad72d4"],
  ["DEX", "dx", "#4f83d8"],
];

export const BOARD_VALUE_FIELDS: readonly string[] = [
  "name",
  "level",
  "play_time",
  "hth",
  "int",
  "str",
  "dex",
];

/** `EmpireFlag_A/B/C` at half scale (`selectcharacterwindow.py:142-172`). */
const EMPIRE_FLAG_URLS = [
  "",
  `${INTRO_ART}/empire_flag_a.png`,
  `${INTRO_ART}/empire_flag_b.png`,
  `${INTRO_ART}/empire_flag_c.png`,
] as const;

export function empireName(empire: number): string {
  return EMPIRE_NAMES[empire] ?? "";
}

export function jobName(job: number): string {
  return JOB_NAMES[job] ?? "";
}

export function defaultSlot(slots: readonly CharacterSlot[]): number {
  for (const slot of slots) if (slot.filled) return slot.slot;
  return slots.length > 0 ? slots[0].slot : 0;
}

/** Whether any slot on this account holds a character. */
export function hasAnyCharacter(slots: readonly CharacterSlot[]): boolean {
  return slots.some((slot) => slot.filled);
}

export function resumableSlot(
  slots: readonly CharacterSlot[],
  selected: number | null,
): number | null {
  if (selected === null) return null;
  const found = slots.find((slot) => slot.slot === selected);
  return found?.filled === true ? selected : null;
}

const STYLE_ID = "charselect-style";
const DEFAULT_PORTRAIT_PALETTE: CreaturePalette = { body: "#8a7a63", dark: "#554b3d" };

/** The animation clock this screen's preview bodies keep, one per slot. High
 *  enough not to collide with any world entity id. */
const PREVIEW_ENTITY_ID = 0x4000; // above any server id in the selection screen, as charcreate.ts:923 does for its preview

const PREVIEW_HEADING_STEPS = 3_328; // 292.5 deg of the 4096-step heading (metin3/net/schema.py:161): front-on to the camera

export const PREVIEW_FOV_RAD = (26 * Math.PI) / 180;
export const PREVIEW_HEADROOM = 1.26; // measured fit: the model at 86% of the panel (tools/e2e/charselect_frame.mjs:1)
export const PREVIEW_SPAN_RATIO = 0.72; // measured fit, same check (tools/e2e/charselect_frame.mjs:1)
/** Used only until the pack answers `heightMm`; an adult body in this pack is
 *  about 1.6 m and this is that, rounded up rather than invented. */
export const PREVIEW_FALLBACK_HEIGHT_MM = 1_700; // a warrior body's height from the pack until it is resident (client/src/render/creaturemesh.ts:1)

export interface PreviewFraming {
  /** The world z the camera looks at: the middle of the body. */
  readonly centreMm: number;
  /** Half the world height the camera sees at the model's distance. */
  readonly halfHeightMm: number;
  readonly distanceMm: number;
  readonly fovRad: number;
}

export function previewFraming(heightMm: number, aspect: number): PreviewFraming {
  const height = heightMm > 0 ? heightMm : PREVIEW_FALLBACK_HEIGHT_MM;
  const safeAspect = aspect > 0 ? aspect : 1;
  const halfWidth = (height * PREVIEW_SPAN_RATIO) / 2;
  let halfHeight = (height * PREVIEW_HEADROOM) / 2;
  // A panel too narrow to hold the body's width at that framing: widen the
  // vertical extent until the horizontal one fits, rather than cropping arms.
  if (halfHeight * safeAspect < halfWidth) halfHeight = halfWidth / safeAspect;
  return {
    centreMm: height / 2,
    halfHeightMm: halfHeight,
    distanceMm: halfHeight / Math.tan(PREVIEW_FOV_RAD / 2),
    fovRad: PREVIEW_FOV_RAD,
  };
}

function portraitView(heightMm: number, aspect: number): Mat4 {
  const fit = previewFraming(heightMm, aspect);
  const view = lookAt([0, -fit.distanceMm, fit.centreMm], [0, 0, fit.centreMm], [0, 0, 1]);
  const projection = perspective(fit.fovRad, aspect, 100, fit.distanceMm * 4);
  return multiply(projection, view);
}

const STYLE = `
/* The select screen, redesigned.
 *
 * **What it replaced, and why.** Until this rewrite the screen was the 2014
 * layout transcribed literally: an 800x600 authoring box, centred and scaled to
 * whatever the window happened to be (\`tan(atan2(...))\`), with the 208x323
 * \`character_board\` at 8.125%/36.6667% and every child positioned in the
 * uiscript's own pixels. It was faithful and it did not work, and a person
 * playing the deployed build said so on 4 Sep 2026: "Character selector looks
 * weird. Also, the character is without hair and outside the frame."
 *
 * The three things that report names are three different faults:
 *
 *  * **Outside the frame.** The preview's WebGL canvas was never pinned to the
 *    preview: \`CreatureMeshRenderer\` styles its own canvas \`position: fixed;
 *    100vw x 100vh\` (\`creaturemesh.ts:1669\`) and inserts it lazily on the first
 *    \`beginFrame\`, so the class this file added at *construction* time landed on
 *    nothing. A 483x384 drawing buffer was then stretched across the whole
 *    window and the one drawn body -- placed at \`(index - (count-1)/2) * 1350\`
 *    mm, which for slot 0 of four is 2025 mm to the left of the camera -- came
 *    out at three times its intended size against the left edge of the screen,
 *    head above the top of the viewport. See \`paintLineup\`, which pins the
 *    layer after \`beginFrame\` the way \`charcreate.ts\` already did.
 *  * **Without hair.** Nothing on this screen ever asked for \`PART_HAIR\`. See
 *    \`paintLineup\` again.
 *  * **Weird.** Everything below. A 0.49x scale of an 800x600 design on a 390px
 *    phone is 5.4px body text and a 12px-tall Start button; the class name plate
 *    floated over the sky with nothing under it; the slot "cards" were four
 *    transparent hit rectangles hovering over the middle of the painting; the
 *    kingdom name was clipped to "Shinsoo Kingd...".
 *
 * **What it is now.** One card in the language the login screen was restyled
 * into: \`--intro-*\` colours, the shared four-step type scale and 4px spacing
 * rhythm (\`intro.ts\`), one 3px radius, one focus ring. The model is framed
 * large in the middle of it; the four slots are real cards in a row under it;
 * the character's particulars and the four actions are a column beside it. It
 * is a fluid layout rather than a scaled bitmap of one, so 390px is the same
 * design at a readable size rather than the same design at half size.
 *
 * **What is kept, deliberately.** The board is still \`ui.ThinBoard\`
 * (\`root/ui.py:1839-1873\`) and Start is still \`XLarge_Button_01/02/03.sub\`
 * (\`selectcharacterwindow.py:508-510\`); the four class name plates, the two
 * dragon cyclers (\`936_selectcharacterwindow.py:560-576\`), the four gauge rows
 * and the seven \`character_*_value\` cells are all still here and still say what
 * the shipped pack says. The layout is this build's; the material is the
 * original's.
 */
.charselect {
  z-index: 71;
}

/* A fluid card, not a scaled 800x600 box.
 *
 * The rule this replaces made the anchor exactly 800x600 and scaled it with a
 * transform, which is why the screen was illegible on a phone: a transform
 * scales the *type* too. A card that is 94% of the stage and at most 1000px
 * wide keeps 13px text at 13px on every screen, and the panel below reflows
 * rather than shrinking. */
.charselect .intro-anchor {
  inset: auto;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: min(94%, 940px);
  max-height: 94%;
  max-width: none;
  display: block;
  overflow-y: auto;
  overscroll-behavior: contain;
}

/* The regions, over the painting -- not one slab on top of it.
 *
 * This was a card at first, built exactly like \`.login-card\`: one opaque
 * gradient rectangle holding everything. It was measured and rejected.
 * \`tools/e2e/intro.mjs\` samples the select stage for how much of the painting
 * is still a painting, and a 940x478 card over a 1066x800 stage took the
 * sunset band over the temple gate from 15% amber to 8.6% and the stage's
 * distinct colours from about 2,000 to 1,100 -- it failed both, and it deserved
 * to. The login screen can be a card because its form is 384px of controls over
 * a painting with room to spare; this screen's biggest element is a person
 * standing up, and the right place for a person to stand is the town street
 * the original draws behind them (\`selectcharacterwindow.py:26\`), not a dark
 * box laid over it.
 *
 * So the panel is a grid and nothing else. Each region brings its own surface:
 * the slot cards and the board are legible over the painting on their own, and
 * the preview is transparent with a vignette under the model's feet. */
.charselect-panel {
  position: relative;
  display: grid;
  grid-template-areas: "preview" "slots" "board" "message";
  grid-template-columns: minmax(0, 1fr);
  gap: var(--intro-space-3);
  padding: 0;
  border: 0;
  background: transparent;
  font: var(--intro-text-body) / 1.45 system-ui, -apple-system, "Segoe UI", sans-serif;
  color: var(--intro-ink);
}

/* Above 760px the board becomes a full-height column on the right and the
   preview, the slot row and the message line stack on the left. One
   breakpoint, because there are only two shapes this card has. */
@media (min-width: 760px) {
  .charselect-panel {
    grid-template-areas:
      "preview board"
      "slots   board"
      "message board";
    grid-template-columns: minmax(0, 1fr) 264px;
    grid-template-rows: auto auto 1fr;
    gap: var(--intro-space-4);
  }
}

.charselect-sr-heading {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

/* ------------------------------------------------------------ the preview */

/* Where the character stands. A fixed height in \`clamp\` rather than an aspect
   ratio, so the card's own height never depends on how wide the window is and
   nothing moves when the browser is resized or the pack finishes loading. */
.charselect-roster {
  grid-area: preview;
  position: relative;
  height: clamp(196px, 27vh, 250px);
  /* No box: the model stands on the stage's own painting. A soft vignette under
     it, because the street is busy and a dark body against dark stone needs
     something to separate it. */
  background: radial-gradient(
    58% 62% at 50% 62%,
    rgba(0, 0, 0, 0.5),
    rgba(0, 0, 0, 0) 74%
  );
}

@media (min-width: 760px) {
  .charselect-roster { height: clamp(264px, 44vh, 400px); }
}

/* The ground the model stands on, so a body is not floating in a gradient. */
.charselect-roster::after {
  content: "";
  position: absolute;
  left: 12%;
  right: 12%;
  bottom: 8%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(232, 200, 119, 0.22) 30%,
    rgba(232, 200, 119, 0.22) 70%,
    transparent
  );
}

.charselect-lineup {
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

/* The WebGL layer, pinned to this box. \`CreatureMeshRenderer\` stacks its own
   canvas over the whole viewport and inserts it at the first frame, so
   \`paintLineup\` adds this class after \`beginFrame\` and these rules undo the
   stacking. \`!important\` because the renderer sets the same properties
   inline. */
.charselect-lineup-mesh {
  position: absolute !important;
  left: 0 !important;
  top: 0 !important;
  width: 100% !important;
  height: 100% !important;
  z-index: 0 !important;
  pointer-events: none !important;
}

/* The four class plates out of \`select.dds\`
   (\`selectcharacterwindow.py:89-122\`), one coordinate for all four and only
   the selected job's lit -- \`introselect.py\`'s NameList alpha. In the original
   they hang above the board; here they name what is standing in the preview,
   so they hang in the preview's own top-left at a size that reads as a caption
   rather than as a billboard. */
.charselect-class-plates {
  position: absolute;
  left: var(--intro-space-3);
  top: var(--intro-space-2);
  width: 104px;
  height: 60px;
  pointer-events: none;
  z-index: 2;
}
@media (min-width: 760px) {
  .charselect-class-plates { width: 152px; height: 88px; }
}
.charselect-class-plate {
  position: absolute;
  inset: 0;
  background: left center / contain no-repeat;
  opacity: 0;
  /* Two shadows, because the plate is a dark red graphic and it now hangs over
     the painting rather than over a panel: at the desktop size it lands on a lit
     temple wall, where one soft shadow left it barely legible. */
  filter:
    drop-shadow(0 1px 2px rgba(0, 0, 0, 0.95))
    drop-shadow(0 0 12px rgba(0, 0, 0, 0.85));
  transition: opacity 160ms ease;
}
.charselect-class-plate[data-active="true"] { opacity: 1; }

/* The two dragon cyclers, which move the selection one slot
   (\`introselect.py:341-342, 573-579\`). They flank the model at its feet:
   dragon_left ends at x = 450/800 and dragon_right starts at x = 558/800, both
   at y = 505/600 (\`locale/en/ui/selectcharacterwindow.py:498-518\`), 54/800
   either side of a model standing at 504/800. The preview's centre is the
   model's, so each inner edge sits 6.75% of the preview from it. */
.charselect-turn { display: contents; }
.charselect-turn-left,
.charselect-turn-right {
  position: absolute;
  bottom: 4%;
  z-index: 3;
  width: 76px;
  height: 18px;
  min-height: 24px;
  padding: 0;
  border: 0;
  border-image: none;
  border-radius: var(--intro-radius);
  background-color: transparent;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  color: transparent;
  text-shadow: none;
  opacity: 0.72;
  cursor: pointer;
  transition: opacity 140ms ease;
}
.charselect-turn-left:hover,
.charselect-turn-right:hover { opacity: 1; }
.charselect-turn-left {
  right: 56.75%;
  background-image: url("${INTRO_ART}/select_dragon_left.png");
}
.charselect-turn-left:hover { background-image: url("${INTRO_ART}/select_dragon_left_over.png"); }
.charselect-turn-left:active { background-image: url("${INTRO_ART}/select_dragon_left_down.png"); }
.charselect-turn-right {
  left: 56.75%;
  background-image: url("${INTRO_ART}/select_dragon_right.png");
}
.charselect-turn-right:hover { background-image: url("${INTRO_ART}/select_dragon_right_over.png"); }
.charselect-turn-right:active { background-image: url("${INTRO_ART}/select_dragon_right_down.png"); }

/* -------------------------------------------------------------- the slots */

/* Four real cards. They used to be four transparent rectangles laid over the
   turntable, so an empty slot was invisible and a filled one was a faint gold
   outline in the middle of the painting. */
.charselect-slots {
  grid-area: slots;
  display: grid;
  grid-template-columns: repeat(var(--charselect-slot-count, 4), minmax(0, 1fr));
  gap: var(--intro-space-2);
}

/* Two up on a phone: four columns of a 330px card is 78px each, which is not a
   name. */
@media (max-width: 559px) {
  .charselect-slots { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

.charselect-slot {
  display: grid;
  gap: 2px;
  align-content: start;
  min-height: 52px;
  margin: 0;
  padding: var(--intro-space-2) var(--intro-space-3);
  border: 1px solid var(--intro-edge);
  border-radius: var(--intro-radius);
  background: rgba(8, 8, 6, 0.82);
  color: var(--intro-ink);
  font: inherit;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 140ms ease, background-color 140ms ease;
}

.charselect-slot:hover {
  border-color: var(--intro-edge-strong);
  background: rgba(38, 32, 18, 0.86);
}

/* Selected, and said with the gold the rest of the sequence uses for "this
   one" rather than with a second background colour. */
.charselect-slot[aria-pressed="true"] {
  border-color: var(--intro-gold);
  background: rgba(48, 39, 18, 0.9);
  box-shadow: inset 0 0 0 1px rgba(232, 200, 119, 0.26);
}

/* One focus ring for everything focusable on this card, the same one
   \`.login-card\` draws. Outline rather than a border swap, so focus never
   changes anything's size. */
.charselect-slot:focus-visible,
.charselect-play:focus-visible,
.charselect-create:focus-visible,
.charselect-delete:focus-visible,
.charselect-exit:focus-visible,
.charselect-turn-left:focus-visible,
.charselect-turn-right:focus-visible,
.charselect-password:focus-visible,
.charselect-confirm-go:focus-visible {
  outline: 2px solid var(--intro-gold);
  outline-offset: 2px;
}

/* An empty slot is an offer, not a character: dashed, quiet, and it says so. */
.charselect-slot[data-filled="false"] {
  border-style: dashed;
  background: rgba(8, 8, 6, 0.66);
  color: var(--intro-ink-faint);
}
.charselect-slot[data-filled="false"]:hover { color: var(--intro-ink-quiet); }

.charselect-slot-label {
  font-size: var(--intro-text-control);
  font-weight: 600;
  letter-spacing: 0.01em;
  color: inherit;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.charselect-slot[aria-pressed="true"] .charselect-slot-label { color: var(--intro-gold); }

.charselect-slot-meta {
  font-size: var(--intro-text-label);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--intro-ink-dim);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* -------------------------------------------------------------- the board */

/* \`character_board\`: still \`ui.ThinBoard\`, eight 16px edge textures around a
   51%-black interior (\`root/ui.py:1839-1873\`). The frame is a non-consuming
   \`::before\` overlay so the nine-slice sits above the fill without the board
   having to carry a border in its own box model, and so a padding change here
   cannot move the corners. */
.charselect-board {
  grid-area: board;
  position: relative;
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  gap: var(--intro-space-3);
  box-sizing: border-box;
  margin: 0;
  padding: var(--intro-space-4);
  border: 0;
  background-color: var(--intro-board-fill);
  background-image: linear-gradient(rgba(0, 0, 0, 0.38), rgba(0, 0, 0, 0.38));
  color: var(--intro-ink);
}
.charselect-board::before {
  content: "";
  position: absolute;
  inset: 0;
  border: 16px solid transparent;
  border-image-source: var(--intro-frame);
  border-image-slice: var(--intro-frame-slice);
  border-image-repeat: repeat;
  pointer-events: none;
  z-index: 1;
}

/* The kingdom, at the head of the board where the original puts it
   (\`selectcharacterwindow.py:142-172\`: EmpireFlag_A/B/C and EmpireName). The
   name is a row of its own rather than a 90px slot texture, which is what
   clipped "Shinsoo Kingdom" to "Shinsoo Kingd...". */
.charselect-identity {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: var(--intro-space-3);
}

.charselect-empire-flag {
  width: 46px;
  height: 29px;
  object-fit: fill;
  border-radius: 2px;
}
.charselect-empire-flag[hidden] { display: none; }

.charselect-empire-name {
  font-size: var(--intro-text-body);
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--intro-ink-bright);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.charselect-guild-name {
  grid-column: 2;
  margin-top: 1px;
  font-size: var(--intro-text-label);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--intro-ink-faint);
}

/* Name / Level / Playtime -- the first three \`character_*_value\` cells. A
   two-column row each: the label the pack ships, then the value. */
.charselect-facts {
  display: grid;
  gap: var(--intro-space-2);
  margin: 0;
  padding: var(--intro-space-3) 0;
  border-top: 1px solid var(--intro-rule);
  border-bottom: 1px solid var(--intro-rule);
}
.charselect-fact {
  display: grid;
  grid-template-columns: minmax(0, auto) minmax(0, 1fr);
  align-items: baseline;
  gap: var(--intro-space-3);
}
.charselect-fact dt {
  margin: 0;
  font-size: var(--intro-text-label);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--intro-ink-dim);
}
.charselect-fact dd {
  margin: 0;
  font-size: var(--intro-text-body);
  color: var(--intro-ink-bright);
  text-align: right;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.charselect-board-name { font-weight: 600; }
.charselect-board-level,
.charselect-board-playtime { font-variant-numeric: tabular-nums; }

/* The four gauge rows: SELECT_HP, CREATE_SP, SELECT_ATT_GRADE,
   SELECT_DEX_GRADE (\`selectcharacterwindow.py:265-436\`), each fed its stat
   over 90 (\`introselect.py:649-655\`). */
.charselect-board-rows {
  display: grid;
  align-content: start;
  gap: var(--intro-space-2);
  margin: 0;
  padding: 0;
}
.charselect-stat {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) 26px;
  align-items: center;
  gap: var(--intro-space-2);
  margin: 0;
}
.charselect-stat dt {
  margin: 0;
  font-size: var(--intro-text-label);
  letter-spacing: 0.08em;
  color: var(--intro-ink-dim);
}
.charselect-stat-gauge {
  height: 6px;
  margin: 0;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.72);
  box-shadow: inset 0 0 0 1px rgba(130, 112, 75, 0.35);
  overflow: hidden;
}
.charselect-stat-gauge-fill {
  display: block;
  height: 100%;
  width: 0;
  border-radius: 3px;
  background: var(--intro-gold);
  transition: width 160ms ease;
}
.charselect-board-ht,
.charselect-board-iq,
.charselect-board-st,
.charselect-board-dx {
  margin: 0;
  font-size: var(--intro-text-label);
  font-variant-numeric: tabular-nums;
  text-align: right;
  color: var(--intro-ink-bright);
}

/* ------------------------------------------------------------ the actions */

/* Start and Create share one row because the original gives them one
   coordinate and shows exactly one of them (\`selectcharacterwindow.py:439-466\`,
   \`introselect.py:600-673\`); Delete and Close share the row under it. */
.charselect-actions {
  display: grid;
  gap: var(--intro-space-2);
  align-content: end;
}
.charselect-actions-secondary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--intro-space-2);
}

/* \`select_button\` takes XLarge_Button_01/02/03 (\`:508-510\`) and only
   \`delete_button\` and \`exit_button\` take Large_Button_* (\`:535-550\`), which is
   why the primary action wears a different texture from the other two. */
.charselect-play,
.charselect-create {
  height: var(--intro-control);
  min-height: var(--intro-control);
  padding: 0 var(--intro-space-3);
  border: 0 solid transparent;
  border-left-width: 12px;
  border-right-width: 12px;
  border-image-source: url("${INTRO_ART}/button_xlarge.png");
  border-image-slice: 0 12 fill;
  border-image-width: 0 12px;
  border-image-repeat: stretch;
  background: transparent;
  color: var(--intro-gold);
  font: inherit;
  font-size: var(--intro-text-control);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.85);
  cursor: pointer;
}
.charselect-play:hover:not(:disabled),
.charselect-create:hover:not(:disabled) {
  border-image-source: url("${INTRO_ART}/button_xlarge_over.png");
  color: var(--intro-gold-bright);
}
.charselect-play:active:not(:disabled),
.charselect-create:active:not(:disabled) {
  border-image-source: url("${INTRO_ART}/button_xlarge_down.png");
  transform: translateY(1px);
}
.charselect-play[hidden],
.charselect-create[hidden] { display: none; }

.charselect-delete,
.charselect-exit {
  min-height: 34px;
  padding: 0 var(--intro-space-2);
  font-size: var(--intro-text-body);
}
.charselect-delete[hidden] { display: none; }

/* Disabled says so in the ink and not only in the alpha: a 45%-opacity gold
   button over a painting still reads as pressable. \`.login-card\` makes the
   same correction for the same reason. */
.charselect-play:disabled,
.charselect-create:disabled {
  border-image-source: url("${INTRO_ART}/button_xlarge.png");
  color: var(--intro-ink-faint);
  cursor: default;
}

/* ------------------------------------------------- messages and the modal */

.charselect-message {
  grid-area: message;
  display: grid;
  gap: var(--intro-space-2);
  align-content: start;
}

.charselect-error {
  margin: 0;
  font-size: var(--intro-text-body);
}
.charselect-error:empty { display: none; }

/* The status line sits on the painting like everything else here, so it brings
   its own dark strip -- \`.intro-error\` already does, and a bare grey sentence
   over a lit town street is not a sentence anyone can read. */
.charselect-status {
  margin: 0;
  padding: 6px 9px;
  border-left: 2px solid var(--intro-edge-strong);
  background: rgba(8, 8, 6, 0.82);
  color: var(--intro-ink-quiet);
  font-size: var(--intro-text-body);
}
.charselect-status:empty { display: none; }

/* Deleting asks for the account password, over a scrim so the question cannot
   be mistaken for one more control on the card. */
.charselect-confirm {
  position: fixed;
  inset: 0;
  z-index: 6;
  display: grid;
  place-items: center;
  padding: var(--intro-space-4);
  background: rgba(3, 3, 2, 0.72);
}
.charselect-confirm[hidden] { display: none; }
.charselect-confirm-card {
  display: grid;
  gap: var(--intro-space-3);
  width: min(360px, 100%);
  padding: var(--intro-space-4);
  border: 1px solid rgba(180, 85, 60, 0.5);
  border-radius: var(--intro-radius);
  background: linear-gradient(180deg, rgba(34, 12, 8, 0.97), rgba(14, 7, 5, 0.98));
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.7);
}
.charselect-confirm-title {
  margin: 0;
  font-size: var(--intro-text-title);
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--intro-danger-ink);
}
.charselect-confirm p {
  margin: 0;
  font-size: var(--intro-text-body);
  color: var(--intro-ink);
}
.charselect-confirm-what { color: var(--intro-ink-bright); }
.charselect-password {
  width: 100%;
  height: var(--intro-control);
  box-sizing: border-box;
  padding: 0 var(--intro-space-3);
  border: 1px solid var(--intro-edge);
  border-radius: var(--intro-radius);
  background: var(--intro-field);
  color: var(--intro-ink-bright);
  font: inherit;
  font-size: var(--intro-text-control);
}
.charselect-confirm-go {
  min-height: var(--intro-control);
  font-size: var(--intro-text-control);
}

/* Nothing on this screen moves for someone who has asked it not to. The 3D
   preview is the game itself and keeps its idle; every transition here is
   decoration and goes. */
@media (prefers-reduced-motion: reduce) {
  .charselect-class-plate,
  .charselect-slot,
  .charselect-stat-gauge-fill,
  .charselect-turn-left,
  .charselect-turn-right {
    transition: none;
  }
  .charselect-play:active:not(:disabled),
  .charselect-create:active:not(:disabled) {
    transform: none;
  }
}
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export class CharacterSelectScreen {
  private readonly root: HTMLElement;
  private readonly list: HTMLElement;
  private readonly lineup: HTMLCanvasElement;
  /** The preview box. Carries `data-model-key`/`data-hair-key` so a check can
   *  read which parts the last frame actually drew. */
  private readonly roster: HTMLElement;
  private readonly lineupContext: CanvasRenderingContext2D | null;
  private readonly meshes: CreatureMeshRenderer | null;
  private readonly play: HTMLButtonElement;
  private readonly create: HTMLButtonElement;
  private readonly remove: HTMLButtonElement;
  private readonly error: HTMLElement;
  private readonly confirm: HTMLElement;
  private readonly password: HTMLInputElement;
  private readonly confirmButton: HTMLButtonElement;
  private readonly status: HTMLElement;
  private readonly classPlates: HTMLElement[];

  /** The last frame the server sent. Every request is composed out of it. */
  private frame: Characters | null = null;

  /** Which slot is highlighted. Null until a frame has arrived to choose from. */
  private chosen: number | null = null;

  /** True between sending and the server answering, so a second click cannot
   *  become a second request. Cleared by the next frame or by a refusal. */
  private pending = false;
  private animationFrame: number | null = null;

  constructor(
    container: HTMLElement,
    private readonly actions: CharacterSelectActions = {},
  ) {
    const doc = container.ownerDocument;
    // The shared chrome first: this screen's own rules read its custom
    // properties, and a stylesheet that arrived after them would repaint.
    installIntroStyle(doc);
    installStyle(doc);

    // The letterboxed 4:3 stage with `select.jpg` in it -- the town street at
    // dusk that `selectcharacterwindow.py:26` draws behind this screen, and the
    // same wrapper `login.ts` and `charcreate.ts` use with their own backdrop.
    const { ground, anchor } = introGround(doc, "select");
    this.root = ground;
    this.root.classList.add("charselect");
    this.root.hidden = true;
    anchor.innerHTML = TEMPLATE;
    container.append(this.root);

    this.list = this.must(".charselect-slots");
    this.roster = this.must(".charselect-roster");
    this.lineup = this.must<HTMLCanvasElement>(".charselect-lineup");
    let lineupContext: CanvasRenderingContext2D | null = null;
    try {
      lineupContext = this.lineup.getContext("2d", { alpha: true });
    } catch {
      // DOM-only environments may expose a canvas without a 2D context.
    }
    this.lineupContext = lineupContext;
    let meshes: CreatureMeshRenderer | null = null;
    try {
      meshes = new CreatureMeshRenderer(this.lineup);
      // The renderer's own canvas is *not* pinned here. It does not exist in
      // the document yet -- see `pinMeshLayer`, which does it after the first
      // `beginFrame`, and which is the fix for the model being drawn across the
      // whole window instead of inside this panel.
    } catch {
      // Canvas2D remains the visual fallback when WebGL2 is unavailable.
    }
    this.meshes = meshes;
    this.play = this.must<HTMLButtonElement>(".charselect-play");
    this.create = this.must<HTMLButtonElement>(".charselect-create");
    this.remove = this.must<HTMLButtonElement>(".charselect-delete");
    this.error = this.must(".charselect-error");
    this.confirm = this.must(".charselect-confirm");
    this.password = this.must<HTMLInputElement>(".charselect-password");
    this.confirmButton = this.must<HTMLButtonElement>(".charselect-confirm-go");
    this.status = this.must(".charselect-status");
    this.classPlates = Array.from(
      this.root.querySelectorAll<HTMLElement>(".charselect-class-plate"),
    );

    // `dragon_left`/`dragon_right` (936_selectcharacterwindow.py:563-576) wired
    // to DecreaseSlotIndex/IncreaseSlotIndex (introselect.py:341-342, 573-579).
    this.must<HTMLButtonElement>(".charselect-turn-left").addEventListener("click", () => {
      this.cycleSlot(-1);
    });
    this.must<HTMLButtonElement>(".charselect-turn-right").addEventListener("click", () => {
      this.cycleSlot(1);
    });
    this.must<HTMLButtonElement>(".charselect-exit").addEventListener("click", () => {
      if (!this.pending) this.actions.onExit?.();
    });
    // `introselect.py:775-777` exposes the window-level exit hook.
    this.root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      if (!this.pending) this.actions.onExit?.();
    });

    this.play.addEventListener("click", () => this.sendPlay());
    this.create.addEventListener("click", () => this.sendCreate());
    this.remove.addEventListener("click", () => this.openConfirm());
    this.confirmButton.addEventListener("click", () => this.sendDelete());

    for (const event of ["keydown", "keyup", "keypress"]) {
      this.password.addEventListener(event, (raw) => raw.stopPropagation());
    }

    // The original introselect handles left/right at the screen level and
    // moves the selected slot (`introselect.py:680-688`).
    this.root.addEventListener("keydown", (event) => {
      const slotIndex = ["1", "2", "3", "4"].indexOf(event.key);
      if (slotIndex >= 0) {
        const slot = this.frame?.slots[slotIndex];
        if (slot === undefined) return;
        event.preventDefault();
        this.chosen = slot.slot;
        this.error.textContent = "";
        this.closeConfirm();
        this.paint();
        return;
      }
      // `introselect.py:689-696`: scan code 28 is Return.
      if (event.key === "Enter") {
        if (this.confirm.hidden === false) return;
        const slot = this.current();
        if (slot === null) return;
        event.preventDefault();
        if (slot.filled) this.sendPlay();
        else this.sendCreate();
        return;
      }
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      this.cycleSlot(event.key === "ArrowLeft" ? -1 : 1);
    });
  }

  private must<T extends Element>(selector: string): T {
    const found = this.root.querySelector<T>(selector);
    if (found === null) throw new Error(`charselect template is missing ${selector}`);
    return found as T;
  }

  update(frame: Characters): void {
    this.frame = frame;
    this.pending = false;
    this.status.textContent = "";
    this.closeConfirm();

    const stillThere =
      this.chosen !== null &&
      frame.slots.some((slot) => slot.slot === this.chosen);
    if (!stillThere) this.chosen = defaultSlot(frame.slots);

    this.root.hidden = false;
    this.paint();
  }

  /** Show a refusal, and leave the highlighted slot where it is. */
  fail(text: string): void {
    this.release();
    this.error.textContent = text;
  }

  /** Unlock the screen after a refusal the 2014 client showed nothing for. */
  release(): void {
    this.pending = false;
    this.status.textContent = "";
    this.refreshButtons();
  }

  /** Take the screen down. */
  dismiss(): void {
    this.root.hidden = true;
    this.closeConfirm();
    if (this.animationFrame !== null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  /** True while the screen is up, which is what the boot sequence waits on. */
  get open(): boolean {
    return !this.root.hidden;
  }

  /** Which slot is highlighted, for a test and for the creation screen. */
  get slot(): number | null {
    return this.chosen;
  }

  private cycleSlot(delta: -1 | 1): void {
    const slots = this.frame?.slots ?? [];
    if (slots.length === 0) return;
    const current = Math.max(0, slots.findIndex((slot) => slot.slot === this.chosen));
    const next = (current + delta + slots.length) % slots.length;
    this.chosen = slots[next].slot;
    this.error.textContent = "";
    this.closeConfirm();
    this.paint();
  }

  // ------------------------------------------------------------------ private

  private paint(): void {
    const frame = this.frame;
    if (frame === null) return;

    this.list.replaceChildren();
    const doc = this.root.ownerDocument;
    const count = Math.max(1, frame.slots.length);
    // A custom property rather than an inline `grid-template-columns`, because
    // the inline value would beat the phone breakpoint that puts four slots on
    // two rows -- the shape of the row is the stylesheet's decision and how
    // many there are is the frame's.
    this.list.style.setProperty("--charselect-slot-count", String(count));
    for (const slot of frame.slots) {
      const card = doc.createElement("button");
      card.type = "button";
      card.className = "charselect-slot";
      card.dataset.slot = String(slot.slot);
      card.dataset.race = String(slot.race);
      card.dataset.job = String(slot.job);
      card.dataset.filled = String(slot.filled);
      card.setAttribute("aria-pressed", String(slot.slot === this.chosen));
      // The card says what it is in two visible lines, and the same sentence
      // the screen-reader-only span used to carry is now its accessible name.
      // `tools/e2e/charselect.mjs` reads the card's text for the character's
      // name, its class and the word "Empty", and all three are still in it.
      card.setAttribute(
        "aria-label",
        slot.filled
          ? `${slot.name}, ${jobName(slot.job)} level ${slot.level}`
          : `Empty slot ${slot.slot + 1}`,
      );
      const label = elem(doc, "span", "charselect-slot-label");
      label.textContent = slot.filled ? slot.name : SELECT_TEXT.emptySlotCard;
      const meta = elem(doc, "span", "charselect-slot-meta");
      meta.textContent = slot.filled
        ? `${jobName(slot.job)} \u00b7 ${SELECT_TEXT.level} ${slot.level}`
        : SELECT_TEXT.create;
      card.append(label, meta);
      card.addEventListener("click", () => {
        this.chosen = slot.slot;
        this.error.textContent = "";
        this.closeConfirm();
        this.paint();
      });
      this.list.append(card);
    }

    this.paintBoard();
    this.paintLineup();
    this.refreshButtons();
  }

  private paintLineup(): void {
    const slot = this.current();

    const ctx = this.lineupContext;
    const cssWidth = this.lineup.clientWidth || 640;
    const cssHeight = this.lineup.clientHeight || 320;
    const dpr = globalThis.devicePixelRatio || 1;
    if (ctx !== null) {
      this.lineup.width = Math.round(cssWidth * dpr);
      this.lineup.height = Math.round(cssHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssWidth, cssHeight);
    }

    const mesh = this.meshes;
    const shown = slot !== null && slot.filled ? slot : null;
    // `MakeCharacter(index, id, name, race, form, hair)` dresses the select model
    // in the slot's saved wMainPart and wHairPart (`introselect.py:235-243`); an
    // armour with no model of its own falls back to the bare body, as the world
    // does (`entities.ts:bodyKeyFor`).
    const bodyKey =
      mesh !== null && shown !== null
        ? (shown.main_part !== 0 ? mesh.bodyModelKey(shown.main_part, shown.race) : null)
          ?? mesh.baseBodyKey(shown.race)
        : null;
    let drewMesh = false;
    if (mesh !== null) {
      // Unconditional, so a slot that is empty or still streaming clears last
      // frame's body rather than leaving it standing under someone else's name.
      mesh.beginFrame(Math.round(cssWidth * dpr), Math.round(cssHeight * dpr));
      this.pinMeshLayer();
      if (shown !== null && bodyKey !== null && mesh.status(bodyKey).kind === "ready") {
        const hairKey = mesh.hairModelKey(shown.race, shown.hair_part);
        const hairReady = hairKey !== null && mesh.status(hairKey).kind === "ready";
        const heightMm = Math.max(
          mesh.heightMm(bodyKey) ?? 0,
          hairReady ? mesh.heightMm(hairKey) ?? 0 : 0,
        );
        const viewProj = portraitView(heightMm, cssWidth / Math.max(1, cssHeight));
        const entityId = PREVIEW_ENTITY_ID + shown.slot;
        mesh.draw(
          bodyKey, entityId, 0, 0, 0, PREVIEW_HEADING_STEPS, Gait.IDLE, viewProj,
          DEFAULT_PORTRAIT_PALETTE,
        );
        if (hairReady) {
          // The body's own clock and the body's own skeleton, on a derived id
          // so the hair pass cannot rewrite the body's animation state --
          // `entities.ts:drawDefaultHair` argues that at length.
          mesh.draw(
            hairKey, hairEntityId(entityId), 0, 0, 0, PREVIEW_HEADING_STEPS, Gait.IDLE,
            viewProj, DEFAULT_PORTRAIT_PALETTE, null, bodyKey, entityId,
          );
        }
        this.roster.dataset.modelKey = bodyKey;
        this.roster.dataset.hairKey = hairReady ? hairKey : "";
        drewMesh = true;
      }
    }
    if (!drewMesh) {
      delete this.roster.dataset.modelKey;
      delete this.roster.dataset.hairKey;
    }

    // The Canvas2D silhouette, for a browser with no WebGL2 and for a race this
    // pack has no body for. Centred on the same point the camera looks at.
    if (ctx !== null && shown !== null && !drewMesh && (mesh === null || bodyKey === null)) {
      drawCreature(ctx, "humanoid", {
        x: cssWidth / 2,
        y: cssHeight * 0.56,
        radius: Math.max(36, Math.min(90, cssHeight * 0.3)),
        angle: 0,
        distanceMm: 0,
        resting: true,
        palette: DEFAULT_PORTRAIT_PALETTE,
      } satisfies CreatureFrame);
    }
    this.startLineupAnimation();
  }

  private pinMeshLayer(): void {
    const layer = this.lineup.previousElementSibling;
    if (!(layer instanceof HTMLElement) || layer.classList.contains("charselect-lineup-mesh")) {
      return;
    }
    layer.classList.add("charselect-lineup-mesh");
  }

  private startLineupAnimation(): void {
    if (this.animationFrame !== null || typeof requestAnimationFrame !== "function") return;
    this.animationFrame = requestAnimationFrame(() => {
      this.animationFrame = null;
      if (!this.root.hidden) this.paintLineup();
    });
  }

  private paintBoard(): void {
    const slot = this.current();
    const filled = slot !== null && slot.filled;
    const empire = this.frame?.empire ?? 0;
    const flag = this.must<HTMLImageElement>(".charselect-empire-flag");
    flag.dataset.empire = String(empire);
    const flagUrl = EMPIRE_FLAG_URLS[empire] ?? "";
    if (flagUrl) {
      flag.hidden = false;
      flag.src = flagUrl;
    } else {
      flag.hidden = true;
      flag.removeAttribute("src");
    }
    this.must(".charselect-empire-name").textContent = empireName(empire);
    // The legacy board's GuildName slot is always painted; this protocol has
    // no guild-name field on CharacterSlot, so retain its honest no-guild
    // wording rather than guessing a character's membership.
    this.must(".charselect-guild-name").textContent = SELECT_TEXT.noGuild;
    this.must(".charselect-board-name").textContent = filled ? slot.name : "";
    this.must(".charselect-board-level").textContent = filled ? String(slot.level) : "";
    this.must(".charselect-board-playtime").textContent = filled
      ? String(slot.playtime)
      : "";

    const job = filled ? slot.job : -1;
    for (const plate of this.classPlates) {
      plate.dataset.active = String(Number(plate.dataset.job) === job);
    }

    for (const [stat, field, colour] of BOARD_STAT_ROWS) {
      const value = filled ? slot[field] : null;
      const cell = this.must<HTMLElement>(`.charselect-board-${field}`);
      cell.textContent = value === null ? "" : String(value);
      cell.dataset.stat = stat;
      const fill = this.must<HTMLElement>(`.charselect-stat-${field} .charselect-stat-gauge-fill`);
      // `introselect.py:649-655` feeds each legacy Gauge with its stat / 90.
      fill.style.width = `${value === null ? 0 : Math.max(0, Math.min(100, (value / 90) * 100))}%`;
      fill.style.background = colour;
    }
  }

  /** The highlighted slot's row, or null. */
  private current(): CharacterSlot | null {
    const frame = this.frame;
    if (frame === null || this.chosen === null) return null;
    return frame.slots.find((slot) => slot.slot === this.chosen) ?? null;
  }

  private refreshButtons(): void {
    const slot = this.current();
    const filled = slot !== null && slot.filled;
    this.play.hidden = !filled;
    this.remove.hidden = !filled;
    this.create.hidden = filled;
    this.play.disabled = this.pending || !filled;
    this.remove.disabled = this.pending || !filled;
    // Creation is offered on an *empty* highlighted slot only, and refused
    // outright when the shard has closed it -- `Characters.may_create` is the
    // original's own `g_BlockCharCreation` (`input_login.cpp:425-429`).
    this.create.disabled =
      this.pending || slot === null || filled || !(this.frame?.may_create ?? false);
  }

  private sendPlay(): void {
    const slot = this.current();
    if (slot === null || !slot.filled || this.pending) return;
    this.pending = true;
    this.error.textContent = "";
    this.refreshButtons();
    this.actions.onPlay?.(slot.slot);
  }

  private sendCreate(): void {
    const slot = this.current();
    if (slot === null || slot.filled || this.pending) return;
    this.pending = true;
    this.error.textContent = "";
    this.refreshButtons();
    this.actions.onCreate?.(slot.slot);
  }

  private openConfirm(): void {
    const slot = this.current();
    // `RequestDeleteCharacter` answers an empty slot with SELECT_EMPTY_SLOT
    // rather than silently doing nothing (`introselect.py:494-503`).
    if (slot === null || !slot.filled) {
      if (slot !== null) this.error.textContent = SELECT_TEXT.emptySlot;
      return;
    }
    this.confirm.hidden = false;
    this.must<HTMLElement>(".charselect-confirm-what").textContent = slot.name;
    this.password.value = "";
    this.password.focus();
  }

  private closeConfirm(): void {
    this.confirm.hidden = true;
    this.password.value = "";
  }

  private sendDelete(): void {
    const slot = this.current();
    const secret = this.password.value;
    if (slot === null || !slot.filled || this.pending) return;
    if (secret.length === 0) {
      this.error.textContent = "Type your account password to confirm.";
      return;
    }
    this.pending = true;
    this.error.textContent = "";
    // SELECT_DELEING, which `introselect.py:502-503` pops the moment the packet
    // leaves.
    this.status.textContent = SELECT_TEXT.deleting;
    this.refreshButtons();
    this.actions.onDelete?.(slot.slot, secret);
  }
}

const TEMPLATE = `
  <div class="charselect-panel">
    <h1 class="intro-title charselect-title charselect-sr-heading">Choose a character</h1>

    <div class="charselect-roster">
      <canvas class="charselect-lineup" aria-hidden="true"></canvas>

      <!-- Four class plates on one coordinate, only the selected job's lit
           (selectcharacterwindow.py:89-122; introselect.py NameList alpha). -->
      <div class="charselect-class-plates" aria-hidden="true">
        <div class="charselect-class-plate" data-job="0"
          style="background-image:url('${INTRO_ART}/name_warrior.png')"></div>
        <div class="charselect-class-plate" data-job="1"
          style="background-image:url('${INTRO_ART}/name_assassin.png')"></div>
        <div class="charselect-class-plate" data-job="2"
          style="background-image:url('${INTRO_ART}/name_sura.png')"></div>
        <div class="charselect-class-plate" data-job="3"
          style="background-image:url('${INTRO_ART}/name_shaman.png')"></div>
      </div>

      <div class="charselect-turn">
        <button type="button" class="intro-button intro-button-quiet charselect-turn-left"
          aria-label="Previous character"></button>
        <button type="button" class="intro-button intro-button-quiet charselect-turn-right"
          aria-label="Next character"></button>
      </div>
    </div>

    <div class="charselect-slots"></div>

    <section class="charselect-board" aria-label="Character">
      <div class="charselect-identity">
        <img class="charselect-empire-flag" alt="" hidden>
        <div class="charselect-empire-name" aria-label="Empire"></div>
        <div class="charselect-guild-name" aria-label="Guild">${SELECT_TEXT.noGuild}</div>
      </div>

      <dl class="charselect-facts">
        <div class="charselect-fact charselect-fact-name">
          <dt class="charselect-field-name">${SELECT_TEXT.name}</dt>
          <dd class="charselect-board-name"></dd>
        </div>
        <div class="charselect-fact charselect-fact-level">
          <dt class="charselect-field-level">${SELECT_TEXT.level}</dt>
          <dd class="charselect-board-level"></dd>
        </div>
        <div class="charselect-fact charselect-fact-playtime">
          <dt class="charselect-field-playtime">${SELECT_TEXT.playtime}</dt>
          <dd class="charselect-board-playtime"></dd>
        </div>
      </dl>

      <dl class="charselect-board-rows">
        <div class="charselect-stat charselect-stat-ht">
          <dt>HP</dt>
          <dd class="charselect-stat-gauge" aria-hidden="true">
            <span class="charselect-stat-gauge-fill"></span>
          </dd>
          <dd class="charselect-board-ht"></dd>
        </div>
        <div class="charselect-stat charselect-stat-iq">
          <dt>INT</dt>
          <dd class="charselect-stat-gauge" aria-hidden="true">
            <span class="charselect-stat-gauge-fill"></span>
          </dd>
          <dd class="charselect-board-iq"></dd>
        </div>
        <div class="charselect-stat charselect-stat-st">
          <dt>STR</dt>
          <dd class="charselect-stat-gauge" aria-hidden="true">
            <span class="charselect-stat-gauge-fill"></span>
          </dd>
          <dd class="charselect-board-st"></dd>
        </div>
        <div class="charselect-stat charselect-stat-dx">
          <dt>DEX</dt>
          <dd class="charselect-stat-gauge" aria-hidden="true">
            <span class="charselect-stat-gauge-fill"></span>
          </dd>
          <dd class="charselect-board-dx"></dd>
        </div>
      </dl>

      <div class="charselect-actions">
        <button type="button" class="intro-button intro-button-primary charselect-play" disabled>${SELECT_TEXT.start}</button>
        <button type="button" class="intro-button intro-button-quiet charselect-create" disabled>${SELECT_TEXT.create}</button>
        <div class="charselect-actions-secondary">
          <button type="button" class="intro-button intro-button-danger charselect-delete" disabled>${SELECT_TEXT.delete}</button>
          <button type="button" class="intro-button intro-button-quiet charselect-exit">${SELECT_TEXT.exit}</button>
        </div>
      </div>
    </section>

    <div class="charselect-message">
      <p class="intro-error charselect-error"></p>
      <p class="charselect-status" role="status" aria-live="polite"></p>
    </div>

    <div class="charselect-confirm" hidden>
      <div class="charselect-confirm-card" role="dialog" aria-modal="true"
        aria-label="${SELECT_TEXT.deleteTitle}">
        <p class="charselect-confirm-title">${SELECT_TEXT.deleteTitle}</p>
        <p>${SELECT_TEXT.deleteReally} <strong class="charselect-confirm-what"></strong>
           Type your account password to confirm &mdash; the character leaves the
           slot and its name is free again.</p>
        <input class="intro-field charselect-password" type="password" maxlength="128"
          autocapitalize="none" autocorrect="off" spellcheck="false">
        <button type="button" class="intro-button intro-button-danger charselect-confirm-go">Delete for good</button>
      </div>
    </div>
  </div>
`;
